/**
 * Wie er verhuurt: particulier of vanuit een onderneming.
 *
 * Dit staat op het instellingenscherm maar gaat over de **hele account** en niet
 * over de gekozen woning. Dat onderscheid staat er met zoveel woorden bij, want het
 * is precies de verwarring die ontstaat zodra iets naast de instellingen van een
 * residence komt te staan.
 *
 * Drie dingen die geen opmaak zijn:
 *
 * 1. **Wijzigen en corrigeren zijn twee knoppen.** Wie een BV opricht, legt een
 *    nieuwe periode vast vanaf een datum; wie zich verschrijft, zet de bestaande
 *    recht. Het verschil bepaalt hoe de verhuurdagen straks worden geteld, dus het
 *    hoort een keuze te zijn en geen gok van het systeem.
 * 2. **De historie staat er.** Niet omdat iemand erom vroeg, maar omdat het antwoord
 *    op "waarom werd deze boeking geweigerd" erin ligt: onder welk regime viel de
 *    verhuurder toen.
 * 3. **Niets ingevuld is een zichtbare toestand.** Een verhuurder zonder profiel is
 *    niet stilzwijgend particulier; het scherm zegt dat er nog niets is vastgelegd.
 */
import { useState } from 'react'
import type { FormEvent, ReactElement } from 'react'

import { useFormatters } from '@siteshowroom/i18n/react'
import type { Formatters } from '@siteshowroom/i18n'

import { useTranslate } from '../lib/i18n-provider'
import { correctTenantProfile, createTenantProfile } from '../lib/tenant-client'
import type {
  TenantFailure,
  TenantKind,
  TenantProfile,
  TenantProfileState,
} from '../lib/tenant-client'

/** Een ingangsdatum is een kalenderdatum; in UTC gelezen én getoond (§17.6). */
function formatCalendarDate(formatters: Formatters, value: string): string {
  return formatters.formatDate(`${value}T00:00:00Z`, { timeZone: 'UTC' })
}

function today(): string {
  const now = new Date()
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000)
  return local.toISOString().slice(0, 10)
}

interface Draft {
  kind: TenantKind
  legalName: string
  registrationNumber: string
  vatNumber: string
  addressLine1: string
  postalCode: string
  locality: string
  country: string
  effectiveFrom: string
}

function toDraft(profile: TenantProfile | null): Draft {
  return {
    kind: profile?.kind ?? 'private',
    legalName: profile?.legalName ?? '',
    registrationNumber: profile?.registrationNumber ?? '',
    vatNumber: profile?.vatNumber ?? '',
    addressLine1: profile?.addressLine1 ?? '',
    postalCode: profile?.postalCode ?? '',
    locality: profile?.locality ?? '',
    country: profile?.country ?? 'NL',
    effectiveFrom: profile?.effectiveFrom ?? today(),
  }
}

export function CompanySection({
  state,
  busy,
  onChanged,
  onFailure,
}: {
  readonly state: TenantProfileState
  readonly busy: boolean
  readonly onChanged: () => Promise<void>
  readonly onFailure: (error: TenantFailure) => void
}): ReactElement {
  const t = useTranslate()
  const formatters = useFormatters()

  const [draft, setDraft] = useState<Draft | null>(null)
  /** De id die wordt rechtgezet; null betekent: nieuwe periode. */
  const [correctingId, setCorrectingId] = useState<string | null>(null)
  const [working, setWorking] = useState(false)

  const current = state.current

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault()
    if (draft === null) {
      return
    }

    setWorking(true)
    const body = {
      kind: draft.kind,
      // Bij particulier gaan de bedrijfsvelden leeg de deur uit, ongeacht wat er nog
      // in het formulier staat: de API weigert ze anders, en terecht.
      legalName: draft.kind === 'business' ? draft.legalName.trim() : '',
      registrationNumber: draft.kind === 'business' ? draft.registrationNumber.trim() : '',
      vatNumber: draft.kind === 'business' ? draft.vatNumber.trim() : '',
      addressLine1: draft.addressLine1.trim(),
      postalCode: draft.postalCode.trim(),
      locality: draft.locality.trim(),
      country: draft.country.trim().toUpperCase(),
      effectiveFrom: draft.effectiveFrom,
    }

    const result =
      correctingId === null
        ? await createTenantProfile(body)
        : await correctTenantProfile(correctingId, body)

    if (result.ok) {
      setDraft(null)
      setCorrectingId(null)
    } else {
      onFailure(result.error)
    }
    await onChanged()
    setWorking(false)
  }

  return (
    <section className="page__section">
      <h2 className="page__sectionTitle">{t('dashboard.company.title')}</h2>
      {/* Dit gaat over de hele account, niet over de gekozen woning. */}
      <p className="prose">{t('dashboard.company.intro')}</p>

      {current === null ? (
        <div className="notice notice--action" role="status">
          <p className="notice__title">{t('dashboard.company.missing')}</p>
        </div>
      ) : (
        <dl className="facts">
          <div className="facts__row">
            <dt>{t('dashboard.company.field.kind')}</dt>
            <dd>{t(`dashboard.company.kind.${current.kind}`)}</dd>
          </div>
          {current.kind === 'business' && (
            <>
              <div className="facts__row">
                <dt>{t('dashboard.company.field.legalName')}</dt>
                {/* Gegevens van de onderneming: data, niet vertaald. */}
                <dd>{current.legalName}</dd>
              </div>
              <div className="facts__row">
                <dt>{t('dashboard.company.field.registrationNumber')}</dt>
                <dd>{current.registrationNumber}</dd>
              </div>
              {current.vatNumber !== '' && (
                <div className="facts__row">
                  <dt>{t('dashboard.company.field.vatNumber')}</dt>
                  <dd>{current.vatNumber}</dd>
                </div>
              )}
            </>
          )}
          <div className="facts__row">
            <dt>{t('dashboard.company.field.effectiveFrom')}</dt>
            <dd>{formatCalendarDate(formatters, current.effectiveFrom)}</dd>
          </div>
        </dl>
      )}

      {/* De historie: hier ligt het antwoord op "onder welk regime viel ik toen". */}
      {state.history.length > 1 && (
        <>
          <h3 className="page__sectionTitle">{t('dashboard.company.history.title')}</h3>
          <ul className="tier-list">
            {state.history.map((profile) => {
              const kind = t(`dashboard.company.kind.${profile.kind}`)
              return (
                <li key={profile.id}>
                  {t('dashboard.company.history.entry', {
                    kind,
                    from: formatCalendarDate(formatters, profile.effectiveFrom),
                  })}
                </li>
              )
            })}
          </ul>
        </>
      )}

      {draft === null && (
        <div className="request-panel__actions">
          <button
            className="button"
            disabled={busy || working}
            onClick={() => {
              setDraft(toDraft(current))
              setCorrectingId(null)
            }}
            type="button"
          >
            {current === null
              ? t('dashboard.company.action.add')
              : t('dashboard.company.action.change')}
          </button>
          {current !== null && (
            <button
              className="button button--quiet"
              disabled={busy || working}
              onClick={() => {
                setDraft(toDraft(current))
                setCorrectingId(current.id)
              }}
              type="button"
            >
              {t('dashboard.company.action.correct')}
            </button>
          )}
        </div>
      )}

      {draft !== null && (
        <form
          className="request-panel"
          onSubmit={(event) => {
            void submit(event)
          }}
        >
          <h3 className="request-panel__title">
            {correctingId === null
              ? t('dashboard.company.editor.change')
              : t('dashboard.company.editor.correct')}
          </h3>
          <p className="request-panel__hint">
            {correctingId === null
              ? t('dashboard.company.editor.changeHint')
              : t('dashboard.company.editor.correctHint')}
          </p>

          <label className="field">
            <span className="field__label">{t('dashboard.company.field.kind')}</span>
            <select
              className="field__input"
              disabled={working}
              onChange={(event) => {
                setDraft({ ...draft, kind: event.target.value as TenantKind })
              }}
              value={draft.kind}
            >
              <option value="private">{t('dashboard.company.kind.private')}</option>
              <option value="business">{t('dashboard.company.kind.business')}</option>
            </select>
            <span className="field__hint">{t(`dashboard.company.kind.${draft.kind}Hint`)}</span>
          </label>

          {draft.kind === 'business' && (
            <div className="field-grid">
              <label className="field">
                <span className="field__label">{t('dashboard.company.field.legalName')}</span>
                <input
                  className="field__input"
                  disabled={working}
                  maxLength={200}
                  onChange={(event) => {
                    setDraft({ ...draft, legalName: event.target.value })
                  }}
                  type="text"
                  value={draft.legalName}
                />
              </label>
              <label className="field">
                <span className="field__label">
                  {t('dashboard.company.field.registrationNumber')}
                </span>
                <input
                  className="field__input"
                  disabled={working}
                  inputMode="numeric"
                  maxLength={40}
                  onChange={(event) => {
                    setDraft({ ...draft, registrationNumber: event.target.value })
                  }}
                  type="text"
                  value={draft.registrationNumber}
                />
                <span className="field__hint">
                  {t('dashboard.company.field.registrationNumberHint')}
                </span>
              </label>
              <label className="field">
                <span className="field__label">{t('dashboard.company.field.vatNumber')}</span>
                <input
                  className="field__input"
                  disabled={working}
                  maxLength={40}
                  onChange={(event) => {
                    setDraft({ ...draft, vatNumber: event.target.value })
                  }}
                  type="text"
                  value={draft.vatNumber}
                />
                <span className="field__hint">{t('dashboard.company.field.vatNumberHint')}</span>
              </label>
            </div>
          )}

          <div className="field-grid">
            <label className="field">
              <span className="field__label">{t('dashboard.company.field.addressLine1')}</span>
              <input
                className="field__input"
                disabled={working}
                maxLength={200}
                onChange={(event) => {
                  setDraft({ ...draft, addressLine1: event.target.value })
                }}
                type="text"
                value={draft.addressLine1}
              />
            </label>
            <label className="field">
              <span className="field__label">{t('dashboard.company.field.postalCode')}</span>
              <input
                className="field__input"
                disabled={working}
                maxLength={20}
                onChange={(event) => {
                  setDraft({ ...draft, postalCode: event.target.value })
                }}
                type="text"
                value={draft.postalCode}
              />
            </label>
            <label className="field">
              <span className="field__label">{t('dashboard.company.field.locality')}</span>
              <input
                className="field__input"
                disabled={working}
                maxLength={120}
                onChange={(event) => {
                  setDraft({ ...draft, locality: event.target.value })
                }}
                type="text"
                value={draft.locality}
              />
            </label>
            <label className="field">
              <span className="field__label">{t('dashboard.company.field.effectiveFrom')}</span>
              <input
                className="field__input"
                disabled={working}
                onChange={(event) => {
                  setDraft({ ...draft, effectiveFrom: event.target.value })
                }}
                type="date"
                value={draft.effectiveFrom}
              />
              <span className="field__hint">
                {correctingId === null
                  ? t('dashboard.company.field.effectiveFromHint')
                  : t('dashboard.company.field.effectiveFromCorrectHint')}
              </span>
            </label>
          </div>

          <div className="request-panel__actions">
            <button className="button" disabled={working} type="submit">
              {working ? t('dashboard.company.action.saving') : t('dashboard.company.action.save')}
            </button>
            <button
              className="button button--quiet"
              disabled={working}
              onClick={() => {
                setDraft(null)
                setCorrectingId(null)
              }}
              type="button"
            >
              {t('dashboard.company.action.cancel')}
            </button>
          </div>
        </form>
      )}
    </section>
  )
}
